import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mic, MicOff, Sparkles, Settings, Globe, Stethoscope, User, FileText, Volume2, Square, Pause, Play, BookOpen, HelpCircle, Coins, Download, Mail, Shield, Keyboard, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VOICE_TRANSLATE_COMMAND_ENABLED } from '@/lib/feature-flags';


export default function UserManualPage() {
  const navigate = useNavigate();

  const handleBack = () => {
    // Zonder query-parameters, net als het instellingenscherm: de sessie zit in een
    // cookie. Deze pagina probeerde de tokens uit haar eigen URL terug te geven aan
    // het opnamescherm, maar die staan er niet meer in -- er kwam `/?` uit.
    navigate('/');
  };

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="px-3 py-2 bg-primary/10 border-b flex items-center gap-2 shrink-0">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <BookOpen className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Gebruikershandleiding</span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
          
          {/* Intro */}
          <section>
            <h1 className="text-xl font-bold text-foreground mb-2">Welkom bij iSOFT Voice-to-SOEP</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Deze handleiding helpt u snel op weg met het opnemen van consulten, het genereren van SOEP-verslagen 
              en het verzenden naar uw EPD. Hieronder vindt u een stapsgewijze uitleg van alle functies.
            </p>
          </section>

          {/* 1 – Starten */}
          <Section
            number={1}
            title="Opname starten"
            icon={<Mic className="h-4 w-4" />}
          >
            <p>
              Druk op de grote <strong>microfoonknop</strong> om de opname te starten. 
              De app begint direct met het transcriberen van het gesprek in real-time.
            </p>
            <Tip>
              Zorg ervoor dat uw microfoon correct is ingesteld en dat u toestemming heeft gegeven voor microfoongebruik in uw browser.
            </Tip>
          </Section>

          {/* 2 – Sprekerherkenning */}
          <Section
            number={2}
            title="Sprekerherkenning"
            icon={<Stethoscope className="h-4 w-4" />}
          >
            <p>
              De app herkent automatisch verschillende sprekers in het gesprek. Na het starten van de opname 
              verschijnen sprekerblokken met namen als <em>Spreker 1</em>, <em>Spreker 2</em>, etc.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>
                <strong>Klik op een sprekernaam</strong> om uzelf als <em>Zorgverlener</em> te markeren
              </li>
              <li>
                De zorgverlener krijgt een <Stethoscope className="h-3 w-3 inline text-primary" /> icoon, 
                de patiënt een <User className="h-3 w-3 inline text-muted-foreground" /> icoon
              </li>
              <li>
                U kunt ook <strong>herkenningszinnen</strong> instellen (bijv. &quot;Hier spreekt de arts&quot;) 
                zodat de app u automatisch herkent
              </li>
            </ul>
          </Section>

          {/* 3 – Taal */}
          <Section
            number={3}
            title="Taal selecteren"
            icon={<Globe className="h-4 w-4" />}
          >
            <p>
              Bovenaan het opnamescherm vindt u <strong>twee taalkiezers</strong>:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Taal zorgverlener</strong> – de taal die u als zorgverlener spreekt (standaard Nederlands)</li>
              <li><strong>Taal patiënt</strong> – de taal die de patiënt spreekt</li>
            </ul>
            <p>
              De app luistert naar beide talen tegelijk en herkent automatisch welke taal wordt gesproken.
              Wanneer de talen verschillen, wordt de <strong>Vertaal</strong>-knop actief.
            </p>
          </Section>

          {/* 4 – Pauzeren & stoppen */}
          <Section
            number={4}
            title="Pauzeren, vertalen en stoppen"
            icon={<Square className="h-4 w-4" />}
          >
            <p>
              Tijdens de opname ziet u onderaan het scherm de volgende knoppen:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><Pause className="h-3 w-3 inline" /> <strong>Pauzeren</strong> – de opname wordt tijdelijk gestopt</li>
              <li><Play className="h-3 w-3 inline" /> <strong>Hervatten</strong> – ga verder waar u gebleven was</li>
              <li><Globe className="h-3 w-3 inline" /> <strong>Vertaal</strong> – vertaal en lees ongelezen berichten voor (alleen actief bij verschillende talen)</li>
              <li><Square className="h-3 w-3 inline" /> <strong>Stop & genereer SOEP</strong> – beëindig de opname en genereer het verslag</li>
            </ul>
          </Section>

          {/* 5 – SOEP genereren */}
          <Section
            number={5}
            title="SOEP-verslag genereren"
            icon={<Sparkles className="h-4 w-4" />}
          >
            <p>
              Na het stoppen van de opname wordt automatisch een SOEP-verslag gegenereerd. 
              De AI analyseert het transcript en maakt een gestructureerd verslag:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>S</strong>ubjectief – klachten en anamnese van de patiënt</li>
              <li><strong>O</strong>bjectief – bevindingen bij lichamelijk onderzoek</li>
              <li><strong>E</strong>valuatie – diagnose / differentiaaldiagnose</li>
              <li><strong>P</strong>lan – behandelplan, medicatie, verwijzingen</li>
            </ul>
            <Tip>
              De AI houdt rekening met uw rol (huisarts, POH, etc.) en verslagleggingsstijl. 
              U kunt dit aanpassen via Instellingen.
            </Tip>
          </Section>

          {/* 6 – SOEP beoordelen */}
          <Section
            number={6}
            title="SOEP beoordelen en verzenden"
            icon={<FileText className="h-4 w-4" />}
          >
            <p>
              Na het genereren krijgt u een <strong>beoordelingsscherm</strong> te zien. Hier kunt u:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Het SOEP-verslag controleren en indien nodig bewerken</li>
              <li>ICPC-codes bekijken en aanpassen (indien automatisch suggesties aan staat)</li>
              <li>Het verslag <strong>verzenden</strong> naar uw EPD</li>
              <li>Of <strong>annuleren</strong> om terug te keren naar het opnamescherm</li>
            </ul>
          </Section>

          {/* 7 – Vertaalfunctie */}
          <Section
            number={7}
            title="Live vertaling & voorlezen"
            icon={<Volume2 className="h-4 w-4" />}
          >
            <p>
              Wanneer de <strong>zorgverlener</strong> en <strong>patiënt</strong> een verschillende taal spreken, 
              kunt u de vertaalfunctie gebruiken. De vertaling werkt <strong>bi-directioneel</strong>:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Wat de zorgverlener zegt wordt vertaald naar de taal van de patiënt</li>
              <li>Wat de patiënt zegt wordt vertaald naar de taal van de zorgverlener</li>
            </ul>
            <p>
              De vertaling wordt <strong>niet automatisch</strong> gestart, maar pas na een trigger:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              {VOICE_TRANSLATE_COMMAND_ENABLED && (
                <li>Het ingesproken <strong>vertaal-commandowoord</strong> (bijv. &quot;Lees voor&quot;)</li>
              )}
              <li>De <Globe className="h-3 w-3 inline" /> <strong>Vertaal</strong>-knop onderaan het scherm</li>
              <li>Een <strong>sneltoets</strong> op het toetsenbord of de remote presenter (zie hieronder)</li>
            </ul>
            <p>
              Na het triggeren worden alle nog niet vertaalde berichten vertaald en vervolgens 
              via spraaksynthese (TTS) voorgelezen in de juiste taal.
            </p>
            <Tip>
              U kunt de laatst vertaalde batch herhalen via de <strong>PageUp</strong>-toets.
            </Tip>
          </Section>

          {/* 8 – Sneltoetsen & Remote Presenter */}
          <Section
            number={8}
            title="Sneltoetsen & Remote Presenter"
            icon={<Keyboard className="h-4 w-4" />}
          >
            <p>
              De app kan bediend worden via het toetsenbord of een <strong>remote presenter</strong> (draadloze afstandsbediening). 
              Dit is handig als u tijdens het consult niet achter uw computer zit.
            </p>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left p-2 font-medium">Toets</th>
                    <th className="text-left p-2 font-medium">Actie</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">PageDown / →</td>
                    <td className="p-2">Vertaal & lees voor</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">PageUp</td>
                    <td className="p-2">Herhaal laatste vertaling</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">ESC / F5</td>
                    <td className="p-2">Pauzeer opname</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">. (punt)</td>
                    <td className="p-2">Hervat opname</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-mono text-xs">←</td>
                    <td className="p-2">Wissel pauzeren / hervatten</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <Tip>
              Sneltoetsen werken alleen wanneer de focus niet in een tekstveld ligt (bijv. bij het bewerken van instellingen).
            </Tip>
          </Section>

          {/* 9 – Instellingen */}
          <Section
            number={9}
            title="Persoonlijke instellingen"
            icon={<Settings className="h-4 w-4" />}
          >
            <p>
              Klik op het <Settings className="h-3 w-3 inline" /> <strong>tandwiel-icoon</strong> rechtsboven 
              om uw persoonlijke instellingen te openen. Hier kunt u configureren:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li><strong>Weergavenaam</strong> – hoe u in het systeem wordt weergegeven</li>
              <li><strong>Zorgverlener rol</strong> – Huisarts, Assistente, POH (incl. specialisaties)</li>
              <li><strong>Verslagleggingsstijl</strong> – Beknopt, Gemiddeld of Uitgebreid</li>
              <li><strong>Herkenningszinnen</strong> – zinnen waarmee de AI u herkent als arts</li>
              {VOICE_TRANSLATE_COMMAND_ENABLED && (
                <li><strong>Vertaal-trigger</strong> – het zinnetje dat vertaling activeert (bijv. &quot;Lees voor&quot;)</li>
              )}
              <li><strong>Persoonlijk profiel</strong> – vrije tekst voor aanvullende AI-instructies</li>
              <li><strong>ICPC suggesties</strong> – automatische ICPC-code voorstellen aan/uit</li>
            </ul>
          </Section>

          {/* 10 – Kosten */}
          <Section
            number={10}
            title="Kosten en verbruik"
            icon={<Coins className="h-4 w-4" />}
          >
            <p>
              iSOFT Voice-to-SOEP werkt op basis van <strong>Pay per Use</strong>. 
              U betaalt alleen voor daadwerkelijk gebruik – geen abonnement of vaste kosten.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Tijdens de opname ziet u de <strong>kosten in real-time</strong> bovenaan het scherm</li>
              <li>In Instellingen → <strong>Kosten</strong> vindt u uw maandelijks overzicht</li>
              <li>De eerste week na het einde van de maand ontvangt u een factuur</li>
            </ul>
          </Section>

          {/* 11 – E-mail & Verwerkersovereenkomst */}
          <Section
            number={11}
            title="E-mailverificatie & Verwerkersovereenkomst"
            icon={<Shield className="h-4 w-4" />}
          >
            <p>
              Bij de eerste keer gebruik doorloopt u een <strong>onboarding-wizard</strong>. Hierin:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Wordt uw zakelijk e-mailadres geverifieerd via een verificatiecode</li>
              <li>Gaat u akkoord met de <strong>verwerkersovereenkomst</strong> voor AI-diensten</li>
              <li>De verwerkersovereenkomst wordt bij de verificatie-email bijgesloten als PDF</li>
              <li>U kunt de verwerkersovereenkomst ook altijd downloaden via Instellingen</li>
            </ul>
          </Section>

          {/* 12 – Telefonie (SIP) */}
          <Section
            number={12}
            title="Telefonie — bellen vanuit de app (SIP)"
            icon={<Phone className="h-4 w-4" />}
          >
            <p>
              De app bevat een ingebouwde <strong>softphone</strong> die rechtstreeks vanuit uw browser verbindt met de
              SIP-telefooncentrale (FreePBX/Asterisk) van uw praktijk via een beveiligde WebSocket-verbinding (WSS).
              Bellen werkt alleen als uw beheerder de SIP-trunk heeft ingesteld en uw persoonlijke extensie heeft gekoppeld.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>De <strong>praktijkinstellingen</strong> (SIP hostname, WSS-pad, STUN-server, beluitprefix) worden door de admin centraal beheerd</li>
              <li>Uw <strong>persoonlijke extensie en wachtwoord</strong> stelt u zelf in via Instellingen → tab <em>SIP</em></li>
              <li>Tijdens een gesprek ziet u een <Phone className="h-3 w-3 inline" /> belpaneel met nummer-invoer, mute-knop en ophangknop</li>
              <li>Inkomende oproepen op uw extensie verschijnen als pop-up zodra u in de app staat</li>
              <li>De ingestelde <strong>beluitprefix</strong> wordt automatisch voor elk gekozen nummer geplaatst (bv. "0" voor een buitenlijn)</li>
            </ul>
            <Tip>
              Audio (microfoon + luidspreker) verloopt rechtstreeks tussen uw browser en de PBX van de praktijk.
              Er gaat geen gespreksaudio via iSOFT, Lovable of Microsoft Azure — bel-data blijft binnen uw eigen telefonie-infrastructuur.
            </Tip>
          </Section>

          {/* 12 – Tips */}
          <section className="border border-primary/20 bg-primary/5 rounded-lg p-4 space-y-2">
            <h2 className="text-sm font-semibold text-primary flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              Tips voor het beste resultaat
            </h2>
            <ul className="list-disc list-inside space-y-1 text-sm text-foreground">
              <li>Spreek duidelijk en in een rustig tempo</li>
              <li>Houd de microfoon dicht bij u en de patiënt</li>
              <li>Markeer uzelf als zorgverlener voor een nauwkeuriger verslag</li>
              <li>Gebruik herkenningszinnen zodat de AI u automatisch herkent</li>
              <li>Controleer het SOEP-verslag altijd voor verzending</li>
              <li>Stel uw rol en specialisatie in voor een passend verslagformaat</li>
              <li>Gebruik een remote presenter voor handsfree bediening tijdens het consult</li>
            </ul>
          </section>

          {/* Footer */}
          <p className="text-xs text-muted-foreground text-center pt-4 border-t border-border">
            © iSOFT B.V. — Heeft u vragen? Neem contact op met uw systeembeheerder.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---- Sub-components ---- */

function Section({ number, title, icon, children }: { 
  number: number; 
  title: string; 
  icon: React.ReactNode; 
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
        <span className="flex items-center justify-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
          {number}
        </span>
        {icon}
        {title}
      </h2>
      <div className="pl-8 space-y-2 text-sm text-muted-foreground leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 p-2 bg-accent/30 border border-accent rounded text-xs text-foreground flex items-start gap-2">
      <HelpCircle className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}
